import React, { useState, useMemo } from "react";
import { Plus, Pencil, Trash2, Bell } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import DepthAlertSplash from "@/components/drill/DepthAlertSplash";

function AlertItem({ a, onEdit, onDelete }) {
  return (
    <li className="rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-slate-900 dark:text-slate-100 tabular-nums">
              {a.depth} m
            </span>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {a._diff < 0
                ? "passed"
                : `${a._diff.toFixed(2)} m away`}
            </span>
          </div>
          {a.description && (
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5 break-words">
              {a.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg"
            onClick={onEdit}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg text-red-500 hover:text-red-600"
            onClick={onDelete}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </li>
  );
}

export default function AlertsListDialog({
  open,
  onOpenChange,
  alerts,
  currentDepth,
  onAdd,
  onEdit,
  onDelete,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const { upcoming, previous } = useMemo(() => {
    const rawCurrent = Number(currentDepth);
    const current = Number.isFinite(rawCurrent) ? Number(rawCurrent.toFixed(2)) : 0;
    const withDiff = (alerts || []).map((a) => {
      const rawTarget = Number(a.depth);
      const target = Number.isFinite(rawTarget) ? Number(rawTarget.toFixed(2)) : 0;
      return {
        ...a,
        _diff: Number((target - current).toFixed(2)),
      };
    });
    return {
      upcoming: withDiff
        .filter((a) => a._diff >= 0)
        .sort((a, b) => a._diff - b._diff),
      previous: withDiff
        .filter((a) => a._diff < 0)
        .sort((a, b) => b._diff - a._diff),
    };
  }, [alerts, currentDepth]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Predictions</DialogTitle>
            <DialogDescription>
              Ordered by closest to current depth ({Number(currentDepth) || 0} m).
            </DialogDescription>
          </DialogHeader>

          <Button
            onClick={() => {
              setEditTarget(null);
              setAddOpen(true);
            }}
            className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2 w-full"
          >
            <Plus className="w-4 h-4" /> Add prediction
          </Button>

          {upcoming.length === 0 && previous.length === 0 ? (
            <div className="text-center py-10">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2 dark:text-slate-700" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No predictions yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcoming.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                    Upcoming
                  </h3>
                  <ul className="space-y-2">
                    {upcoming.map((a) => (
                      <AlertItem key={a.id} a={a} onEdit={() => { setEditTarget(a); setAddOpen(true); }} onDelete={() => onDelete(a.id)} />
                    ))}
                  </ul>
                </div>
              )}
              {previous.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                    Previous
                  </h3>
                  <ul className="space-y-2">
                    {previous.map((a) => (
                      <AlertItem key={a.id} a={a} onEdit={() => { setEditTarget(a); setAddOpen(true); }} onDelete={() => onDelete(a.id)} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DepthAlertSplash
        open={addOpen}
        onOpenChange={(o) => {
          setAddOpen(o);
          if (!o) setEditTarget(null);
        }}
        initial={editTarget}
        currentDepth={currentDepth}
        onSubmit={(data) => {
          if (editTarget) {
            onEdit(editTarget.id, data);
          } else {
            onAdd(data);
          }
          setAddOpen(false);
          setEditTarget(null);
        }}
        onDelete={
          editTarget
            ? () => {
                onDelete(editTarget.id);
                setAddOpen(false);
                setEditTarget(null);
              }
            : undefined
        }
      />
    </>
  );
}